import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ProvedorContexto } from '@/hooks/useContexto';
import { ProvedorImpressaoExpedicao } from '@/hooks/useImpressaoExpedicao';
import { useSessao } from '@/hooks/useSessao';
import { AppShell } from '@/componentes/layout/AppShell';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { Admin } from '@/paginas/Admin';
import { Analises } from '@/paginas/Analises';
import { Entrar } from '@/paginas/Entrar';
import { Estoque } from '@/paginas/Estoque';
import { Inicio } from '@/paginas/Inicio';
import { Movimentos } from '@/paginas/Movimentos';
import { Negativos } from '@/paginas/Negativos';
import { Transferencias } from '@/paginas/Transferencias';
import { Kardex } from '@/paginas/Kardex';
import { Inventarios } from '@/paginas/Inventarios';
import { Inventario } from '@/paginas/Inventario';
import { InventarioNovoEscopo } from '@/paginas/InventarioNovoEscopo';
import { InventarioConsulta } from '@/paginas/InventarioConsulta';
import { InventarioMapa } from '@/paginas/InventarioMapa';
import { InventarioAntesDepois } from '@/paginas/InventarioAntesDepois';
import { Conferencia } from '@/paginas/Conferencia';
import { Faturamento } from '@/paginas/Faturamento';
import { Romaneios } from '@/paginas/Romaneios';
import { Etiquetas } from '@/paginas/Etiquetas';
import { Expedicao } from '@/paginas/Expedicao';
import { Recebimento } from '@/paginas/Recebimento';
import { Entradas } from '@/paginas/Entradas';
import { Separacao } from '@/paginas/Separacao';
import { Relatorios } from '@/paginas/Relatorios';
import { NaoEncontrado } from '@/paginas/NaoEncontrado';
import { ImpressaoEtiquetas } from '@/paginas/ImpressaoEtiquetas';
import { EtiquetasLista } from '@/paginas/EtiquetasLista';
import { RomaneioA4 } from '@/paginas/RomaneioA4';
import { PapelA4 } from '@/paginas/PapelA4';
import { PdfA4 } from '@/paginas/PdfA4';
import { VersoesColetor } from '@/paginas/VersoesColetor';

function ExigeLogin({ children }: { children: JSX.Element }): JSX.Element {
  const { usuario, carregando } = useSessao();
  const local = useLocation();

  if (carregando) {
    return (
      <div className="space-y-3 p-4">
        <Esqueleto className="h-12 w-full" />
        <Esqueleto className="h-64 w-full" />
      </div>
    );
  }
  // Guarda o destino para voltar exatamente na tela pedida depois do login —
  // links compartilhados não podem cair sempre no dashboard.
  if (!usuario)
    return <Navigate to="/entrar" replace state={{ de: local.pathname + local.search }} />;
  return children;
}

function ExigeAdmin({ children }: { children: JSX.Element }): JSX.Element {
  const { usuario } = useSessao();
  if (!usuario?.adminMaster) return <Navigate to="/" replace />;
  return children;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/entrar" element={<Entrar />} />
      {/* Aberta no Edge, sem a sessão: o token da URL autoriza (ver RomaneioA4). */}
      <Route path="/romaneio-a4/:romaneio" element={<RomaneioA4 />} />
      {/* Idem, para qualquer papel A4 que a tela já montou (ver abrirPapelNoEdge). */}
      <Route path="/papel-a4/:token" element={<PapelA4 />} />
      {/* E para o PDF da API (o DANFE), com o token no próprio endereço (ver abrirPdfNoEdge). */}
      <Route path="/pdf-a4" element={<PdfA4 />} />
      <Route
        element={
          <ExigeLogin>
            <ProvedorContexto>
              {/* Imprime a etiqueta de expedição do crachá em qualquer tela. */}
              <ProvedorImpressaoExpedicao>
                <AppShell />
              </ProvedorImpressaoExpedicao>
            </ProvedorContexto>
          </ExigeLogin>
        }
      >
        <Route path="/" element={<Inicio />} />
        <Route path="/estoque" element={<Estoque />} />
        <Route path="/movimentos" element={<Movimentos />} />
        <Route path="/movimentos/negativos" element={<Negativos />} />
        <Route path="/transferencias" element={<Transferencias />} />
        <Route path="/kardex" element={<Kardex />} />
        <Route path="/inventario" element={<Inventarios />} />
        <Route path="/inventario/:id" element={<Inventario />} />
        <Route path="/inventario/:id/escopo/novo" element={<InventarioNovoEscopo />} />
        <Route path="/inventario/:id/consulta/:consulta" element={<InventarioConsulta />} />
        <Route path="/inventario/:id/mapa" element={<InventarioMapa />} />
        <Route path="/inventario/:id/antes-depois" element={<InventarioAntesDepois />} />
        <Route path="/separacao" element={<Separacao />} />
        <Route path="/conferencia" element={<Conferencia />} />
        <Route path="/faturamento" element={<Faturamento />} />
        <Route path="/romaneios" element={<Romaneios />} />
        <Route path="/etiquetas" element={<Etiquetas />} />
        <Route path="/etiquetas-lista" element={<EtiquetasLista />} />
        <Route path="/expedicao" element={<Expedicao />} />
        <Route path="/entradas" element={<Entradas />} />
        <Route path="/recebimento" element={<Recebimento />} />
        <Route path="/analises" element={<Analises />} />
        <Route path="/relatorios" element={<Relatorios />} />
        <Route path="/impressao-etiquetas" element={<ImpressaoEtiquetas />} />
        <Route path="/versoes-coletor" element={<VersoesColetor />} />
        <Route
          path="/admin"
          element={
            <ExigeAdmin>
              <Admin />
            </ExigeAdmin>
          }
        />
        <Route path="*" element={<NaoEncontrado />} />
      </Route>
    </Routes>
  );
}
